import React, { useEffect, useState } from 'react';
import { getEvents, getEventTypes, getUsers } from '../api';
import { Form } from 'react-bootstrap';
import Pagination from '../components/Global/Pagination';
import SortableHeader from '../components/ui/SortableHeader';
import { useSorting } from '../hooks/useSorting';
import { format, parseISO } from 'date-fns';

const payloadLabels = {
  deliveryDateFrom: 'Desde',
  deliveryDateTo: 'Hasta'
};

const EventsPage = () => {
  // filtros
  const [filters, setFilters] = useState({ eventTypeId: '', userId: '' });

  // listas para dropdowns
  const [eventTypes, setEventTypes] = useState([]);
  const [users, setUsers] = useState([]);

  // datos y paginación
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState();
  
  // ordenamiento
  const { orderBy, orderDesc, handleSort, getSortIcon } = useSorting('timestamp', true);

  // cargar opciones de filtro
  useEffect(() => {
    getEventTypes().then(setEventTypes).catch(console.error);
    getUsers().then(setUsers).catch(console.error);
  }, []);

  // fetch de eventos
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await getEvents(
          {
            eventTypeId: filters.eventTypeId
              ? Number(filters.eventTypeId)
              : undefined,
            userId: filters.userId ? Number(filters.userId) : undefined,
            orderBy,
            orderDesc
          },
          currentPage,
          itemsPerPage
        );
        setEvents(res.items || []);
        setTotalCount(res.totalCount || 0);
      } catch {
        setEvents([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [filters, currentPage, itemsPerPage, orderBy, orderDesc]);

  if (loading) {
    return <div className="container my-4">Cargando eventos…</div>;
  }

  const renderDetails = evt => {
    // Caso especial: llamada a cliente
    if (evt.eventTypeId === 3) {
      let parsed;
      try {
        parsed = JSON.parse(evt.payload);
      } catch {
        parsed = {};
      }
      return (
        <div>
          <p style={{ marginBottom: '0.25rem' }}>
            <strong>Teléfono:</strong> {parsed.phoneNumber || '—'}
          </p>
          <p style={{ marginBottom: 0 }}>
            <strong>Cliente ID:</strong> {evt.entityId}
          </p>
        </div>
      );
    }

    // Caso genérico: mostrar pares clave/valor (sin `group`)
    let parsed;
    try {
      parsed = JSON.parse(evt.payload);
    } catch {
      parsed = null;
    }
    if (!parsed) return evt.payload;

    return (
      <ul style={{ paddingLeft: '1rem', marginBottom: 0 }}>
        {Object.entries(parsed)
          .filter(([k]) => k !== 'group')
          .map(([key, val]) => {
            const label = payloadLabels[key] || key;
            const display = /^\d{4}-\d{2}-\d{2}$/.test(val)
              ? format(parseISO(val), 'dd/MM/yyyy')
              : String(val);
            return (
              <li key={key}>
                <strong>{label}:</strong> {display}
              </li>
            );
          })}
      </ul>
    );
  };

  return (
    <div className="container my-4">
      <h2 className="mb-4">Eventos</h2>

      {/* filtros por dropdown */}
      <div className="d-flex flex-wrap gap-3 mb-4">
        <Form.Group style={{ minWidth: 200 }}>
          <Form.Label>Tipo de Evento</Form.Label>
          <Form.Select
            value={filters.eventTypeId}
            onChange={e => {
              setFilters(f => ({
                ...f,
                eventTypeId: e.target.value
              }));
              setCurrentPage(1);
            }}
          >
            <option value="">Todos</option>
            {eventTypes.map(et => (
              <option key={et.valueId} value={et.valueId}>
                {et.name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group style={{ minWidth: 200 }}>
          <Form.Label>Usuario</Form.Label>
          <Form.Select
            value={filters.userId}
            onChange={e => {
              setFilters(f => ({ ...f, userId: e.target.value }));
              setCurrentPage(1);
            }}
          >
            <option value="">Todos</option>
            {users.map(u => (
              <option key={u.id} value={u.id}>
                {u.username}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </div>

      {/* tabla desktop */}
      <div className="table-responsive d-none d-md-block">
        <table className="table table-striped">
          <thead>
            <tr>
              <SortableHeader column="eventTypeId" onSort={handleSort} getSortIcon={getSortIcon}>Tipo Evento</SortableHeader>
              <SortableHeader column="userId" onSort={handleSort} getSortIcon={getSortIcon}>Usuario</SortableHeader>
              <SortableHeader column="timestamp" onSort={handleSort} getSortIcon={getSortIcon}>Fecha/Hora</SortableHeader>
              <th>Detalles</th>
              <SortableHeader column="indexRoute" onSort={handleSort} getSortIcon={getSortIcon}>Ruta</SortableHeader>
            </tr>
          </thead>
          <tbody>
            {events.map(evt => (
              <tr key={evt.id}>
                <td>{evt.eventType?.name}</td>
                <td>{evt.user?.username || ''}</td>
                <td>
                  {format(parseISO(evt.timestamp), 'dd/MM/yyyy HH:mm')}
                </td>
                <td style={{ maxWidth: 300 }}>{renderDetails(evt)}</td>
                <td>{evt.indexRoute}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* tarjetas móvil */}
      <div className="d-block d-md-none">
        {events.map(evt => (
          <div key={evt.id} className="card mb-2">
            <div className="card-body">
              <p>
                <strong>Tipo:</strong> {evt.eventType?.name}
              </p>
              <p>
                <strong>Usuario:</strong> {evt.user?.username || ''}
              </p>
              <p>
                <strong>Cuando:</strong>{' '}
                {format(parseISO(evt.timestamp), 'dd/MM/yyyy HH:mm')}
              </p>
              <p>
                <strong>Detalles:</strong>
              </p>
              {renderDetails(evt)}
              <p style={{ marginTop: '0.5rem' }}>
                <strong>Ruta:</strong> {evt.indexRoute}
              </p>
            </div>
          </div>
        ))}
      </div>

      {!events.length && (
        <div className="text-center text-muted mt-3">
          No hay eventos con esos filtros.
        </div>
      )}

      {/* paginación */}
      <div className="d-flex justify-content-center mt-4">
        <Pagination
          totalCount={totalCount}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={value => {
            setItemsPerPage(value);
            setCurrentPage(1);
          }}

        />
      </div>
    </div>
  );
};

export default EventsPage;
